import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Clapperboard, LayoutGrid, CalendarDays, Users, ChevronLeft, ChevronRight, MessageSquare, AlertTriangle, Film, Camera } from 'lucide-react';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { PageHeader, EmptyState, ListSkeleton, Avatar } from '../components/ui';
import PersonaPicker from '../components/PersonaPicker';
import { can, RUOLI_SOCIAL, RUOLI_WORKLOAD, ruoloLabel } from '../lib/ruoli';
import { FASI, FASE } from '../lib/produzione';

const MESI = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];
const GIORNI = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];
const PALETTE = ['#00d4ff', '#a855f7', '#10b981', '#f59e0b', '#f43f5e', '#3b82f6', '#14b8a6', '#f97316', '#84cc16', '#ec4899'];
const coloreCliente = (id) => PALETTE[[...(id || '')].reduce((a, c) => a + c.charCodeAt(0), 0) % PALETTE.length];
const pad = (n) => String(n).padStart(2, '0');
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const fmtG = (s) => s ? new Date(String(s).slice(0, 10) + 'T00:00:00').toLocaleDateString('it-IT', { day: 'numeric', month: 'short' }) : '';

const TABS = [
  { k: 'board', label: 'Board', Icon: LayoutGrid },
  { k: 'calendario', label: 'Calendario agenzia', Icon: CalendarDays },
  { k: 'carico', label: 'Carico team', Icon: Users, roles: RUOLI_WORKLOAD },
];

export default function Produzione() {
  const { user } = useAuth();
  const [sp, setSp] = useSearchParams();
  const tab = sp.get('tab') || 'board';
  const [clienti, setClienti] = useState([]);
  const [cliente, setCliente] = useState('');
  const [persona, setPersona] = useState(() => (user && ['videomaker', 'social_manager'].includes(user.ruolo) ? user.id : ''));

  useEffect(() => { api.get('/clienti').then(r => setClienti(r.data || [])).catch(() => {}); }, []);

  const tabs = TABS.filter(t => !t.roles || can(user, t.roles));

  return (
    <div style={{ padding: 'clamp(16px,4vw,24px) clamp(16px,4vw,28px)', maxWidth: 1400, margin: '0 auto' }}>
      <PageHeader title="Produzione" icon={Clapperboard} subtitle="Video e contenuti in lavorazione su tutti i clienti" />

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ display: 'flex', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 9, padding: 3, gap: 3 }}>
          {tabs.map(({ k, label, Icon }) => (
            <button key={k} onClick={() => setSp({ tab: k })}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, padding: '6px 12px', borderRadius: 6, border: 'none', cursor: 'pointer',
                fontWeight: tab === k ? 700 : 500, background: tab === k ? 'var(--cyan-dim)' : 'transparent', color: tab === k ? 'var(--cyan)' : 'var(--text-muted)' }}>
              <Icon size={14} /> {label}
            </button>
          ))}
        </div>
        {tab !== 'carico' && (
          <>
            <button onClick={() => setPersona(p => p === user?.id ? '' : user?.id)}
              style={{ fontSize: 12, padding: '7px 12px', borderRadius: 8, cursor: 'pointer', fontWeight: 600,
                border: `1px solid ${persona === user?.id ? 'var(--cyan)' : 'var(--border)'}`,
                background: persona === user?.id ? 'var(--cyan-dim)' : 'var(--surface)', color: persona === user?.id ? 'var(--cyan)' : 'var(--text-muted)' }}>
              Solo i miei
            </button>
            <PersonaPicker value={persona} onChange={v => setPersona(v || '')} placeholder="Tutte le persone" style={{ width: 'auto', minWidth: 190, fontSize: 12.5, height: 34, padding: '0 10px' }} />
            {tab === 'board' && (
              <select className="eden-input" value={cliente} onChange={e => setCliente(e.target.value)} style={{ width: 'auto', minWidth: 170, fontSize: 12.5, height: 34, padding: '0 10px' }}>
                <option value="">Tutti i clienti</option>
                {clienti.map(c => <option key={c.id} value={c.id}>{c.azienda || c.nome}</option>)}
              </select>
            )}
          </>
        )}
      </div>

      {tab === 'board' && <Board cliente={cliente} persona={persona} />}
      {tab === 'calendario' && <CalendarioAgenzia persona={persona} />}
      {tab === 'carico' && can(user, RUOLI_WORKLOAD) && <CaricoTeam />}
    </div>
  );
}

/* ── Board per fase ───────────────────────────────────────────────────── */
function Board({ cliente, persona }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { success } = useToast();
  const [righe, setRighe] = useState(null);
  const [drag, setDrag] = useState(null);
  const [over, setOver] = useState(null);

  const load = useCallback(() => {
    api.get('/ped/board', { params: { cliente_id: cliente || undefined, persona_id: persona || undefined } })
      .then(r => setRighe(r.data.righe || [])).catch(() => setRighe([]));
  }, [cliente, persona]);
  useEffect(() => { setRighe(null); load(); }, [load]);

  const colonne = [{ key: '', label: 'Da avviare', color: 'var(--text-dim)' }, ...FASI];
  const oggi = ymd(new Date());

  const sposta = async (r, fase) => {
    if (!fase || r.fase_produzione === fase) return;
    if (fase === 'approvato' && !can(user, RUOLI_SOCIAL)) return;
    const prima = righe;
    setRighe(rs => rs.map(x => x.id === r.id ? { ...x, fase_produzione: fase } : x));
    try { await api.patch(`/ped/${r.id}/produzione`, { fase_produzione: fase }); success(`«${r.titolo_post}» → ${FASE[fase].label}`); load(); }
    catch { setRighe(prima); }
  };

  if (!righe) return <ListSkeleton rows={4} />;
  if (!righe.length) return <EmptyState icon={Film} title="Nessun video in lavorazione" hint="I Reel e i Video YT (o i contenuti con una fase di produzione) compaiono qui. Assegna la fase dalla scheda del contenuto." />;

  return (
    <div style={{ display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 12, WebkitOverflowScrolling: 'touch' }}>
      {colonne.map(col => {
        const items = righe.filter(r => (r.fase_produzione || '') === col.key);
        return (
          <div key={col.key || 'none'} className={`eden-board-col${items.length === 0 ? ' vuota' : ''}${over === col.key && drag ? ' drop' : ''}`}
            onDragOver={e => { if (col.key) { e.preventDefault(); setOver(col.key); } }}
            onDragLeave={() => setOver(o => o === col.key ? null : o)}
            onDrop={e => { e.preventDefault(); setOver(null); if (drag) sposta(drag, col.key); setDrag(null); }}
            style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: 8, display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 'calc(100dvh - 240px)', overflowY: 'auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 4px 2px' }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: col.color }} />
              <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text)' }}>{col.label}</span>
              <span style={{ fontSize: 11, color: 'var(--text-dim)' }}>{items.length}</span>
            </div>
            {items.map(r => {
              const montaggioScaduto = r.scadenza_montaggio && r.scadenza_montaggio < oggi && ['script', 'preproduzione', 'riprese', 'montaggio', ''].includes(r.fase_produzione || '');
              return (
                <div key={r.id} className="eden-board-card" draggable onDragStart={() => setDrag(r)} onDragEnd={() => { setDrag(null); setOver(null); }}
                  onClick={() => navigate(`/ped/contenuto/${r.id}`)}
                  style={{ background: 'var(--bg2, var(--bg))', border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden', cursor: 'pointer', opacity: drag?.id === r.id ? 0.5 : 1 }}>
                  {r.thumb_url && <img src={r.thumb_url} alt="" loading="lazy" style={{ width: '100%', height: 90, objectFit: 'cover', display: 'block' }} />}
                  <div style={{ padding: '8px 10px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 3 }}>
                      <span style={{ width: 7, height: 7, borderRadius: '50%', background: coloreCliente(r.cliente_id), flexShrink: 0 }} />
                      <span style={{ fontSize: 10.5, color: 'var(--text-dim)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.cliente_nome} · {r.formato}</span>
                      {r.id_post && <span style={{ marginLeft: 'auto', fontSize: 10, fontFamily: 'monospace', color: 'var(--cyan)' }}>{r.id_post}</span>}
                    </div>
                    <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text)', lineHeight: 1.3 }}>{r.titolo_post}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 7, flexWrap: 'wrap', fontSize: 10.5, color: 'var(--text-dim)' }}>
                      {r.data_pubblicazione && <span title="Pubblicazione">📅 {fmtG(r.data_pubblicazione)}</span>}
                      {r.data_riprese && <span title="Riprese" style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}><Camera size={10} /> {fmtG(r.data_riprese)}</span>}
                      {r.scadenza_montaggio && <span title="Consegna montaggio" style={{ color: montaggioScaduto ? 'var(--red)' : undefined }}>✂ {fmtG(r.scadenza_montaggio)}</span>}
                      {r.ultima_versione && <span>v{r.ultima_versione}</span>}
                      {Number(r.commenti_aperti) > 0 && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2, color: 'var(--yellow)' }}><MessageSquare size={10} /> {r.commenti_aperti}</span>}
                      {r.cliente_appro === 'modifiche' && <span style={{ color: 'var(--yellow)', fontWeight: 700 }}>modifiche cliente</span>}
                      {r.cliente_appro === 'approvato' && <span style={{ color: 'var(--green)', fontWeight: 700 }}>ok cliente</span>}
                      <span style={{ marginLeft: 'auto', display: 'flex', gap: 2 }}>
                        {r.responsabile_nome && <Avatar name={r.responsabile_nome} size={20} />}
                        {r.videomaker_nome && <Avatar name={r.videomaker_nome} size={20} />}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

/* ── Calendario di tutti i clienti ────────────────────────────────────── */
function CalendarioAgenzia({ persona }) {
  const navigate = useNavigate();
  const [mese, setMese] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [righe, setRighe] = useState(null);
  const chiave = `${mese.getFullYear()}-${pad(mese.getMonth() + 1)}`;

  useEffect(() => {
    setRighe(null);
    api.get('/ped/calendario_agenzia', { params: { mese: chiave, persona_id: persona || undefined } })
      .then(r => setRighe(r.data || [])).catch(() => setRighe([]));
  }, [chiave, persona]);

  const perGiorno = useMemo(() => {
    const m = {};
    (righe || []).forEach(r => {
      if (r.data_pubblicazione) (m[r.data_pubblicazione.slice(0, 10)] = m[r.data_pubblicazione.slice(0, 10)] || []).push({ ...r, _tipo: 'pub' });
      if (r.data_riprese) { const k = ymd(new Date(r.data_riprese)); (m[k] = m[k] || []).push({ ...r, _tipo: 'riprese' }); }
    });
    return m;
  }, [righe]);

  const clientiLegenda = useMemo(() => {
    const m = new Map();
    (righe || []).forEach(r => m.set(r.cliente_id, r.cliente_nome));
    return [...m.entries()];
  }, [righe]);

  const primo = (mese.getDay() + 6) % 7;
  const giorniMese = new Date(mese.getFullYear(), mese.getMonth() + 1, 0).getDate();
  const celle = [...Array(primo).fill(null), ...Array.from({ length: giorniMese }, (_, i) => new Date(mese.getFullYear(), mese.getMonth(), i + 1))];
  while (celle.length % 7) celle.push(null);
  const oggi = ymd(new Date());

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
        <button onClick={() => setMese(d => new Date(d.getFullYear(), d.getMonth() - 1, 1))} style={navBtn}><ChevronLeft size={16} /></button>
        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', minWidth: 150, textAlign: 'center' }}>{MESI[mese.getMonth()]} {mese.getFullYear()}</div>
        <button onClick={() => setMese(d => new Date(d.getFullYear(), d.getMonth() + 1, 1))} style={navBtn}><ChevronRight size={16} /></button>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginLeft: 'auto', fontSize: 11.5, color: 'var(--text-muted)' }}>
          {clientiLegenda.map(([id, nome]) => (
            <span key={id} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><span style={{ width: 8, height: 8, borderRadius: 2, background: coloreCliente(id) }} />{nome}</span>
          ))}
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Camera size={11} /> riprese</span>
        </div>
      </div>
      {!righe ? <ListSkeleton rows={5} /> : (
        <div style={{ overflowX: 'auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(120px, 1fr))', gap: 4, minWidth: 860 }}>
            {GIORNI.map(g => <div key={g} style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-dim)', textTransform: 'uppercase', padding: '4px 6px' }}>{g}</div>)}
            {celle.map((d, i) => {
              if (!d) return <div key={i} />;
              const k = ymd(d);
              const items = perGiorno[k] || [];
              return (
                <div key={i} style={{ minHeight: 104, background: 'var(--surface)', border: `1px solid ${k === oggi ? 'var(--cyan)' : 'var(--border)'}`, borderRadius: 8, padding: 5, display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: k === oggi ? 'var(--cyan)' : 'var(--text-muted)' }}>{d.getDate()}</div>
                  {items.map((r, j) => (
                    <button key={j} onClick={() => navigate(`/ped/contenuto/${r.id}`)} title={`${r.cliente_nome} · ${r.formato} · ${r.titolo_post}`}
                      style={{ display: 'flex', alignItems: 'center', gap: 4, textAlign: 'left', fontSize: 10.5, padding: '2px 5px', borderRadius: 5, cursor: 'pointer', border: 'none',
                        background: r._tipo === 'riprese' ? 'var(--bg3)' : `${coloreCliente(r.cliente_id)}22`, color: 'var(--text)',
                        borderLeft: `3px solid ${coloreCliente(r.cliente_id)}`, opacity: r.stato === 'Pubblicato' ? 0.6 : 1 }}>
                      {r._tipo === 'riprese' ? <Camera size={10} style={{ flexShrink: 0 }} /> : r.ora_pubblicazione ? <span style={{ color: 'var(--text-dim)', flexShrink: 0 }}>{r.ora_pubblicazione.slice(0, 5)}</span> : null}
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.titolo_post}</span>
                    </button>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Carico team per settimana ────────────────────────────────────────── */
function CaricoTeam() {
  const [dati, setDati] = useState(null);
  useEffect(() => { api.get('/ped/carico_team').then(r => setDati(r.data)).catch(() => setDati({ persone: [] })); }, []);
  if (!dati) return <ListSkeleton rows={5} />;

  const lunedi = dati.lunedi ? new Date(dati.lunedi + 'T00:00:00') : new Date();
  const settimane = [0, 1, 2, 3].map(i => { const d = new Date(lunedi); d.setDate(d.getDate() + i * 7); return d; });
  const tot = (s) => (s?.uscite || 0) + (s?.riprese || 0) * 2 + (s?.montaggi || 0) * 2 + (s?.task || 0);
  const heat = (n) => n >= 14 ? 'var(--red-dim)' : n >= 8 ? 'var(--yellow-dim)' : n > 0 ? 'var(--green-dim)' : 'transparent';
  const persone = [...(dati.persone || [])].sort((a, b) => tot(b.settimane[0]) + tot(b.settimane[1]) - tot(a.settimane[0]) - tot(a.settimane[1]));

  return (
    <div>
      <div style={{ fontSize: 12, color: 'var(--text-dim)', marginBottom: 10 }}>
        Per ogni settimana: contenuti in uscita, riprese, montaggi da consegnare e task. Riprese e montaggi pesano doppio nel colore.
      </div>
      <div style={{ overflowX: 'auto', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 760, fontSize: 12.5 }}>
          <thead>
            <tr style={{ background: 'var(--bg3)' }}>
              <th style={th}>Persona</th>
              {settimane.map((d, i) => (
                <th key={i} style={th}>{i === 0 ? 'Questa settimana' : i === 1 ? 'Prossima' : `Sett. ${d.toLocaleDateString('it-IT', { day: 'numeric', month: 'short' })}`}</th>
              ))}
              <th style={th}>In ritardo</th>
            </tr>
          </thead>
          <tbody>
            {persone.map(p => (
              <tr key={p.id} style={{ borderTop: '1px solid var(--border)' }}>
                <td style={td}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Avatar name={`${p.nome} ${p.cognome}`} size={26} />
                    <div><div style={{ fontWeight: 600, color: 'var(--text)' }}>{p.nome} {p.cognome}</div><div style={{ fontSize: 11, color: 'var(--text-dim)' }}>{ruoloLabel(p.ruolo)}</div></div>
                  </div>
                </td>
                {p.settimane.map((s, i) => (
                  <td key={i} style={{ ...td, background: heat(tot(s)) }}>
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', color: 'var(--text-muted)' }}>
                      <span title="Contenuti in uscita">📅 {s.uscite}</span>
                      <span title="Riprese">🎥 {s.riprese}</span>
                      <span title="Montaggi da consegnare">✂ {s.montaggi}</span>
                      <span title="Task">☑ {s.task}</span>
                    </div>
                  </td>
                ))}
                <td style={td}>
                  {(p.in_ritardo > 0 || p.montaggi_scaduti > 0) ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: 'var(--red)', fontWeight: 600 }}>
                      <AlertTriangle size={13} /> {p.in_ritardo} contenuti{p.montaggi_scaduti ? ` · ${p.montaggi_scaduti} montaggi` : ''}
                    </span>
                  ) : <span style={{ color: 'var(--text-dim)' }}>—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const navBtn = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, width: 32, height: 32, cursor: 'pointer', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center' };
const th = { textAlign: 'left', padding: '10px 12px', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' };
const td = { padding: '10px 12px', verticalAlign: 'middle' };
